import { Controller, useFormContext } from 'react-hook-form';
import { SimplePanel } from '~/components/common/Panel';
import { Checkbox } from '~/components/ui/forms/Checkbox';
import { Select } from '~/components/ui/forms/select';
import { TagsInput } from '~/components/ui/forms/tags-input';
import { TextInput } from '~/components/ui/forms/text-input';
import { TinyMceInput } from '~/components/ui/forms/tinymce-input';
import { OutcropSelectV4 } from '~/components/upload/outcrop/outcrop-select-v4';
import { ProjectSelect } from '~/components/upload/project/project-select-v4';
import type { VomFormValues } from '~/utils/modules/vom-v4';

export function VomFormFields() {
  const { register, control } = useFormContext<VomFormValues>();

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="space-y-6">
        <SimplePanel title="General Info">
          <div className="space-y-2">
            <TextInput {...register('name')} label="Name" required />

            <Controller
              control={control}
              name="projectId"
              render={({ field }) => (
                <ProjectSelect {...field} label="Project" required />
              )}
            />
            <Controller
              control={control}
              name="outcropId"
              render={({ field }) => (
                <OutcropSelectV4 {...field} label="Outcrop" required />
              )}
            />

            <Select
              {...register('size')}
              label="VOM size"
              options={[
                'small (0-200m)',
                'medium (200-2000m)',
                'large (2000+m)',
              ]}
              required
            />
            <Select
              {...register('smallestVisibleFeature')}
              label="Smallest visible feature"
              required
              options={['< 1cm', '1-5 cm', '5-20 cm', '20-50 cm', '> 50cm'].map(
                opt => ({ value: opt, label: opt }),
              )}
            />
            <TextInput
              {...register('v3GeoId')}
              type="number"
              label={{
                label: 'V3Geo ID',
                helpText:
                  "Enter the ID of the model in V3Geo if applicable. The VO viewer will embed the V3Geo model viewer instead of Safari's.",
              }}
            />
            <Checkbox {...register('approved')} label="Approved" />
          </div>
        </SimplePanel>

        <SimplePanel title="Acquisition and Processing">
          <div className="space-y-2">
            <TextInput
              {...register('acquisitionYear')}
              type="number"
              label="Acquisition year"
              required
            />
            <TextInput
              {...register('aquiringPerson')}
              label="Acquiring person and institute"
              required
            />
            <Select
              {...register('acquisitionType')}
              label="Acquisition type"
              required
              options={[
                'TLS - Terrestrial laser scanning',
                'HLS - Helicopter-based laser scanning',
                'MLS - Mobile phone-based laser scanning',
                'UAV - Drone based photogrammetry',
                'Photogrammetry',
                'other',
                'undefined',
              ]}
            />
            <TextInput
              {...register('acquisitionEquipment')}
              label="Acquisition equipment"
              required
            />
            <TextInput
              {...register('processingYear')}
              type="number"
              label="Processing year"
              required
            />
            <TextInput
              {...register('processingPerson')}
              label="Processing person and institute"
              required
            />
            <TextInput {...register('comments')} label="Comments" />
          </div>
        </SimplePanel>
      </div>

      <div className="space-y-4">
        <SimplePanel title="Description">
          <div className="space-y-2">
            <TinyMceInput {...register('quality')} label="VOM Quality" />
            <TinyMceInput
              {...register('notableAspects')}
              label="Notable aspects"
            />
          </div>
        </SimplePanel>

        <SimplePanel title="Author and Copyright">
          <div className="space-y-2">
            <TagsInput
              name="copyright"
              label="VOM authors who hold copyright"
              mode="array"
              required
            />
            <TagsInput
              name="copyrightLogo"
              label="Links to logos of copyright holders"
              mode="array"
            />
          </div>
        </SimplePanel>
      </div>
    </div>
  );
}
